<script setup>
import { Head, Link } from '@inertiajs/vue3'
import { capitalize, openDialogById, ratio16by9, uuid } from '@/helpers'
import { thumbor } from '@/Frontend/plugins/thumbor/thumbor'
import { useIntersectionObserver } from '@vueuse/core'
import slugify from '@sindresorhus/slugify'

const breadcrumb = defineAsyncComponent(() => import('@components/Breadcrumb.vue'))
const premiumTeaser = defineAsyncComponent(() => import('@components/PremiumTeaser.vue'))
const trialExpirationMessage = defineAsyncComponent(() => import('@components/TrialExpirationMessage.vue'))
const textLabel = defineAsyncComponent(() => import('@/Frontend/plugins/theme/components/labels/LabelTextLabel.vue'))

const props = defineProps({
	article: {
		type: Object,
		default: () => {},
	},
	latestArticles: {
		type: Object,
		default: () => {},
	},
	relatedArticles: {
		type: Object,
		default: () => {},
	},
	restaurantArticles: {
		type: Object,
		default: () => {},
	},
	restaurant: {
		type: Object,
		default: null,
	},
	person: {
		type: Object,
		default: null,
	},
})

const appStore = useAppStore()
const dayjs = inject('dayjs')
const publishedAt = computed(() => date => {
	return dayjs(date).locale(appStore.i18n).format("D MMM 'YY")
})

// Observer for avoiding big DOM size
const target = ref(null)
const targetIsVisible = ref(false)

const { stop } = useIntersectionObserver(target, ([{ isIntersecting }], observerElement) => {
	targetIsVisible.value = isIntersecting
	if (isIntersecting) stop()
})

const render = ref(uuid())
// Watch for content changes i.e. after login
watch(
	() => props.article.render,
	(newVal, oldVal) => {
		// console.log('changed!!')
		render.value = uuid()
	}
)

const articleImg = {
	sm: {
		dpr1: thumbor(props.article.thumbnail).dimension(500, ratio16by9(500)).cropPreset(props.article.thumbnail_cropping).filters('format(webp)').url(),
		dpr2: thumbor(props.article.thumbnail)
			.dimension(500 * 2, ratio16by9(500) * 2)
			.cropPreset(props.article.thumbnail_cropping)
			.filters('format(webp)')
			.url(),
	},
	md: {
		dpr1: thumbor(props.article.thumbnail).dimension(900, ratio16by9(900)).cropPreset(props.article.thumbnail_cropping).filters('format(webp)').url(),
		dpr2: thumbor(props.article.thumbnail)
			.dimension(900 * 2, ratio16by9(900) * 2)
			.cropPreset(props.article.thumbnail_cropping)
			.filters('format(webp)')
			.url(),
	},
}

// Add SEO
const pageMeta = [
	{ tag: 'link', tagItem: 'rel', tagValue: 'canonical', contentItem: 'href', contentValue: `https://lekker.nl${props.article.slug}` },
	{ tag: 'meta', tagItem: 'name', tagValue: 'title', contentItem: 'content', contentValue: props.article.title },
	{ tag: 'meta', tagItem: 'name', tagValue: 'description', contentItem: 'content', contentValue: props.article.description },
	{ tag: 'meta', tagItem: 'name', tagValue: 'keywords', contentItem: 'content', contentValue: props.article.tags },
	{ tag: 'meta', tagItem: 'property', tagValue: 'article:published_time', contentItem: 'content', contentValue: props.article.published_at?.replace(' ', 'T') },
	{ tag: 'meta', tagItem: 'property', tagValue: 'og:title', contentItem: 'content', contentValue: props.article.title },
	{ tag: 'meta', tagItem: 'property', tagValue: 'og:description', contentItem: 'content', contentValue: props.article.description },
	{ tag: 'meta', tagItem: 'property', tagValue: 'og:url', contentItem: 'content', contentValue: `https://lekker.nl${props.article.slug}` },
	{ tag: 'meta', tagItem: 'property', tagValue: 'og:image', contentItem: 'content', contentValue: thumbor(props.article.thumbnail).dimension(900, ratio16by9(900)).filters('format(webp)').url() },
	{ tag: 'meta', tagItem: 'name', tagValue: 'twitter:title', contentItem: 'content', contentValue: `${props.article.title} - Lekker` },
	{ tag: 'meta', tagItem: 'name', tagValue: 'twitter:card', contentItem: 'content', contentValue: 'summary' },
	{ tag: 'meta', tagItem: 'name', tagValue: 'twitter:site', contentItem: 'content', contentValue: '@lekker500' },
]
</script>

<template>
	<Head>
		<title>{{ article.title }}</title>
		<link
			rel="preload"
			as="image"
			:href="articleImg.sm.dpr1"
			:imagesrcset="`${articleImg.sm.dpr1} 1x, ${articleImg.sm.dpr2} 2x`"
			:media="`max-width: ${appStore.breakpoints['sm']}px`"
			type="image/webp"
			fetchpriority="high"
		/>
		<link
			rel="preload"
			as="image"
			:href="articleImg.md.dpr1"
			:imagesrcset="`${articleImg.md.dpr1} 1x, ${articleImg.md.dpr2} 2x`"
			:media="`min-width: ${appStore.breakpoints['sm'] + 1}px`"
			type="image/webp"
			fetchpriority="high"
		/>
		<component
			:is="`${meta.tag}`"
			v-for="(meta, metaIndex) in pageMeta"
			:key="`pagemeta-${metaIndex}`"
			:[meta.tagItem]="meta.tagValue"
			:[meta.contentItem]="meta.contentValue"
		/>

		<component
			:is="`script`"
			type="application/ld+json"
		>
			{{ article.schema }}
		</component>
	</Head>

	<main>
		<article
			class="post"
			:data-article-id="article.id"
		>
			<header class="container-xxl main-wrapper mt-4">
				<div class="row justify-content-center">
					<div class="col-xl-6">
						<client-only>
							<breadcrumb
								:nav-items="[
									{ label: 'Home', route: '/' },
									{ label: article.main_category.name, route: `/${article.main_category.slug}` },
								]"
								:current="article.title"
								class="mb-3"
							/>
						</client-only>
						<text-label
							v-if="article.spotlight"
							class="mb-3"
						/>
						<text-label
							v-if="article.premium === 2"
							size="md"
							class="mb-3"
							:text="$t('Premium')"
						/>

						<h1 class="h2 ff-serif hyphen lh-1">{{ article.title }}</h1>
						<p class="text-neutral-800">
							<time :datetime="article.published_at">{{ publishedAt(article.published_at) }}</time>
							<i class="str-diamond mx-1" />
							<span>{{ article.main_category?.name }}</span>
							<i class="str-diamond mx-1" />
							<Link
								v-if="article.editor_details"
								:href="route('author', { id: article.editor_details.id, slug: article.editor_details.slug })"
								class="text-reset"
							>
								{{ article.editor }}
							</Link>
							<span v-else-if="article.editor">
								{{ article.editor }}
							</span>
						</p>
						<p class="lead">{{ article.description }}</p>
					</div>
				</div>
			</header>
			<section class="container-xxl main-wrapper">
				<div class="row my-3 my-lg-4">
					<div class="col">
						<figure
							class="figure post-main-img d-block mx-n4 mx-lg-auto rounded bg-neutral-300"
							style="aspect-ratio: 16 / 9"
						>
							<picture class="picture">
								<source
									:srcset="`${articleImg.sm.dpr1} 1x, ${articleImg.sm.dpr2} 2x`"
									:media="`(max-width: ${appStore.breakpoints['sm']}px)`"
									type="image/webp"
									width="500"
									:height="ratio16by9(500)"
								/>
								<source
									:srcset="`${articleImg.md.dpr1} 1x, ${articleImg.md.dpr2} 2x`"
									:media="`(min-width: ${appStore.breakpoints['sm'] + 1}px)`"
									type="image/webp"
									width="900"
									:height="ratio16by9(900)"
								/>
								<img
									:src="articleImg.md.dpr1"
									:alt="article.thumbnail_alt ?? ''"
									class="img-fluid bg-neutral-300 rounded-xl w-xl-auto"
									width="900"
									:height="ratio16by9(900)"
									style="min-width: 100%"
									fetchpriority="high"
								/>
							</picture>
						</figure>
					</div>
				</div>

				<div class="row justify-content-center">
					<div
						v-if="article.content"
						:key="render"
						v-html-raw="article.content"
						class="post-contents col-xl-6"
						:class="[article.show_premium_offer ? 'hidden-content-bottom-body' : null]"
					/>
				</div>

				<div
					v-if="article.show_premium_offer"
					class="row justify-content-center"
				>
					<div class="col-xl-6">
						<premium-teaser v-if="article.show_premium_offer">
							<template #left>
								<div class="h4">Dit is een premium artikel</div>
								<auth-check>
									<template #auth="{ user }">
										<p
											v-if="!user.trial_ends_at"
											class="mb-0"
										>
											Wilt u meer lezen? Koop dan de gids of verzilver de krascode die daarbij is meegeleverd.
										</p>
										<p
											v-else-if="user.trial_ends_within_a_week"
											class="mb-0"
										>
											Uw gratis proefperiode digitale toegang zal {{ user.trial_ends_at }} verlopen. Verleng om verder te kunnen lezen.
										</p>
										<p
											v-else-if="user.trial_expired"
											class="mb-0"
										>
											Uw gratis proefperiode is verlopen. Verleng om verder te kunnen lezen.
										</p>
									</template>
									<p class="mb-0">Wilt u meer lezen? Log dan in of koop de gids.</p>
								</auth-check>
							</template>
							<template #right>
								<auth-check>
									<template #auth="{ user }">
										<div v-if="!user.trial_ends_at">
											<Link
												role="button"
												:href="route('coupon.index')"
												class="btn btn-white fw-bold fw-bold w-100 mt-1"
											>
												Ik heb een krascode
											</Link>
											<Link
												class="btn btn-theme-500 fw-bold w-100 mt-1"
												:href="route('order')"
											>
												Bestellen
												<i class="str-arrow-right ms-1" />
											</Link>
										</div>
										<div v-if="user.trial_expired || user.trial_ends_within_a_week">
											<Link
												class="btn btn-outline-neutral-900 fw-bold w-100 mt-1"
												:href="route('digital.extend')"
											>
												Verlengen
												<i class="str-arrow-right ms-1" />
											</Link>
										</div>
										<!-- <pre>{{ user }}</pre> -->
									</template>
									<a
										role="button"
										href="javascript:void(0);"
										class="btn btn-outline-neutral-900 fw-bold w-100"
										@click="openDialogById('login-modal-dialog')"
									>
										{{ $t('Login') }}
									</a>

									<Link
										role="button"
										:href="route('coupon.index')"
										class="btn btn-white fw-bold fw-bold w-100 mt-1"
									>
										Ik heb een krascode
									</Link>
									<Link
										class="btn btn-theme-500 fw-bold w-100 mt-1"
										:href="route('order')"
									>
										Gids bestellen
										<i class="str-arrow-right ms-1" />
									</Link>
								</auth-check>
							</template>
						</premium-teaser>
					</div>
				</div>

				<auth-check>
					<template #auth="{ user }">
						<trial-expiration-message
							v-if="!article.show_premium_offer"
							:user="user"
							style="max-width: 640px; width: 100%"
						/>
					</template>
				</auth-check>
			</section>
			<footer>
				<div class="container-xxl main-wrapper">
					<div class="row justify-content-center">
						<div class="col-xl-6">
							<hr />
							<div class="row">
								<span v-if="article.credit">
									<div class="h5 col-12">
										{{ $t('Credits') }}
									</div>
									<div class="col-12">
										{{ article.credit }}
									</div>
									<hr />
								</span>
								<div
									v-if="article.tags"
									class="col-md-6"
								>
									{{ $t('Tags') }}

									<ul class="list-unstyled d-flex flex-wrap flex-md-column gap-1 gap-md-2 mt-2">
										<li
											v-for="tag in article.tags.split(',')"
											:key="`tag-${tag}`"
										>
											<Link :href="route('tags', { slug: slugify(tag, { lowercase: false, customReplacements: [['&', 'en']] }) })">
												<theme-property-badge hover>{{ capitalize(tag) }}</theme-property-badge>
											</Link>
										</li>
									</ul>
								</div>
								<div class="col-md-6">
									<div v-if="article.photography">
										{{ $t('Photography') }}
										<ul class="list-unstyled d-flex flex-wrap flex-md-column gap-1 gap-md-2 mt-2">
											<li>
												<theme-property-badge>{{ article.photography }}</theme-property-badge>
											</li>
										</ul>
									</div>
									<div v-if="article.video">
										{{ $t('Video') }}
										<ul class="list-unstyled d-flex flex-wrap flex-md-column gap-1 gap-md-2 mt-2">
											<li>
												<theme-property-badge>{{ article.video }}</theme-property-badge>
											</li>
										</ul>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</footer>
		</article>
	</main>

	<div ref="target"></div>

	<aside
		v-if="targetIsVisible"
		class="py-3 py-md-5 bg-neutral-200 mt-3"
	>
		<div class="container-xxl main-wrapper">
			<div
				v-if="restaurant"
				class="row justify-content-center mb-4"
			>
				<div class="col-sm-8 col-md-6 col-lg-4">
					<theme-card-restaurant :item="restaurant" />
				</div>
			</div>

			<div class="row justify-content-center">
				<div
					v-if="restaurantArticles.length > 0"
					class="col-xl-8 mb-3"
				>
					<theme-base-swiper
						v-slot="slotProps"
						tag="section"
						:title="$t('More about :restaurant', { restaurant: restaurant.name })"
						:items="restaurantArticles"
						:columns="{
							xs: 6,
							md: 4,
							lg: 4,
						}"
						url="/nieuws"
						inline
					>
						<theme-card-plain
							tag="article"
							:item="slotProps.item"
							style="--card-aspect-ratio: 1 / 0.75"
						>
							<template #img="{ meta }">
								<thumbor-image
									:use="['sm', 'md']"
									:dimensions="{
										sm: [220, 165],
										md: [275, 205],
									}"
									:img="meta.imgSource"
									:img-alt="meta.imgAlt"
									class="w-100 mb-0"
									:classes="{
										img: 'custom-card-img image-fluid bg-neutral-300 w-100',
									}"
									:crop-preset="meta.imgCropping"
									filters="format(webp)"
								/>
							</template>
						</theme-card-plain>
					</theme-base-swiper>
				</div>

				<div
					v-if="relatedArticles.length > 0"
					class="col-xl-8 mb-3"
				>
					<theme-base-swiper
						v-slot="slotProps"
						tag="section"
						:title="$t('Related news')"
						:items="relatedArticles"
						:columns="{
							xs: 6,
							md: 4,
							lg: 4,
						}"
						url="/nieuws"
						inline
					>
						<theme-card-plain
							tag="article"
							:item="slotProps.item"
							style="--card-aspect-ratio: 1 / 0.75"
						>
							<template #img="{ meta }">
								<thumbor-image
									:use="['sm', 'md']"
									:dimensions="{
										sm: [220, 165],
										md: [275, 205],
									}"
									:img="meta.imgSource"
									:img-alt="meta.imgAlt"
									class="w-100 mb-0"
									:classes="{
										img: 'custom-card-img image-fluid bg-neutral-300',
									}"
									:crop-preset="meta.imgCropping"
									filters="format(webp)"
								/>
							</template>
						</theme-card-plain>
					</theme-base-swiper>
				</div>

				<div
					v-if="latestArticles.length > 0"
					class="col-xl-8"
				>
					<theme-base-swiper
						v-slot="slotProps"
						tag="section"
						:title="$t('Latest news')"
						:items="latestArticles"
						:columns="{
							xs: 6,
							md: 4,
							lg: 4,
						}"
						url="/nieuws"
						inline
					>
						<theme-card-plain
							tag="article"
							:item="slotProps.item"
							style="--card-aspect-ratio: 1 / 0.75"
						>
							<template #img="{ meta }">
								<thumbor-image
									:use="['sm', 'md']"
									:dimensions="{
										sm: [220, 165],
										md: [275, 205],
									}"
									:img="meta.imgSource"
									:img-alt="meta.imgAlt"
									class="w-100 mb-0"
									:classes="{
										img: 'custom-card-img image-fluid bg-neutral-300',
									}"
									:crop-preset="meta.imgCropping"
									filters="format(webp)"
								/>
							</template>
						</theme-card-plain>
					</theme-base-swiper>
				</div>
			</div>
		</div>
	</aside>

	<span
		id="article-code"
		v-html-raw="article.code"
	/>
</template>
